/** Amount → raw number (before source/target multipliers). */
import type { Amount, AmountPerStack, SimContext, Unit, UnitId } from '../contracts';
import { unitEffectiveStats } from './stats';
import { unitOf } from './util';

/**
 * Sum every present term of an Amount, then roll variance. `power` overrides the source's
 * live effective power (aura snapshots). Not rounded: the damage/heal pipeline rounds last.
 */
export function computeAmount(ctx: SimContext, a: Amount, sourceId: UnitId, targetId: UnitId | null, power?: number, spent?: number): number {
  return computeAmountEx(ctx, a, sourceId, targetId, power, spent).amount;
}

/**
 * +lead: rotation rework (F7). Stacks of `ps.auraId` that a per-stack amount reads: on the source
 * (`on: 'self'`) or the affected unit (`'target'`); `fromSelf` (default true) counts only
 * instances cast by the source.
 */
export function perStackCount(ctx: SimContext, ps: AmountPerStack, sourceId: UnitId, targetId: UnitId | null): number {
  const holder: Unit | undefined = unitOf(ctx, ps.on === 'self' ? sourceId : targetId);
  if (!holder) return 0;
  const fromSelf = ps.fromSelf ?? true;
  let n = 0;
  for (const inst of holder.auras) {
    if (inst.auraId === ps.auraId && (!fromSelf || inst.casterId === sourceId)) n += inst.stacks;
  }
  return n;
}

/** computeAmount plus the stacks read by `perStack` (undefined when the amount has none). */
export function computeAmountEx(ctx: SimContext, a: Amount, sourceId: UnitId, targetId: UnitId | null, power?: number, spent?: number): { amount: number; stacks?: number } {
  const s = unitOf(ctx, sourceId);
  const t = unitOf(ctx, targetId);
  let v = a.base ?? 0;
  if (a.coef) v += a.coef * (power ?? (s ? unitEffectiveStats(ctx, s).power : 0));
  if (a.pctSourceMaxHp && s) v += a.pctSourceMaxHp * s.maxHp;
  if (a.pctTargetMaxHp && t) v += a.pctTargetMaxHp * t.maxHp;
  if (a.pctSourceMissingHp && s) v += a.pctSourceMissingHp * Math.max(0, s.maxHp - s.hp); // +specs
  if (a.pctRecentDamageTaken && s) {
    const r = a.pctRecentDamageTaken;
    const since = ctx.now - r.windowMs;
    let taken = 0;
    for (const d of s.recentDamage) if (d.at > since) taken += d.amount;
    let x = taken * r.pct;
    if (r.minPctMaxHp !== undefined) x = Math.max(x, r.minPctMaxHp * s.maxHp);
    v += x;
  }
  let stacks: number | undefined;
  if (a.perStack) {
    stacks = perStackCount(ctx, a.perStack, sourceId, targetId);
    v *= 1 + a.perStack.mult * stacks;
  }
  if (a.perSpent) v *= 1 + a.perSpent * Math.max(0, spent ?? 0); // +mage-specs (S11)
  if (a.variance) v *= ctx.rng.range(1 - a.variance, 1 + a.variance);
  return { amount: Math.max(0, v), stacks };
}
